import React, { useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';

import WaeStandardTextField from '../../../components/WaeStandardTextField/WaeStandardTextField';
import { BLACK, PRIMARY_PURPLE, RED } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import SectionCard from '../../positionTemplate/components/SectionCard';
import SwitchRow from '../../positionTemplate/components/SwitchRow';
import FormFieldRow from '../components/FormFieldRow';
import { textFieldSx } from '../components/formFieldStyles';
import MinutesSelectField from '../components/MinutesSelectField';
import {
  EARLY_PUNCH_WINDOW_OPTIONS,
  getSectionAnchorId,
  PUNCH_DISTANCE_INPUT_PATTERN,
  PUNCH_ROUNDING_OPTIONS,
  SECTION_IDS,
} from '../constants';
import { getPunchDistanceError } from '../helpers/additionalInfoValidation';

const GPS_DISTANCE_ID = 'position-template-gps-distance';
const EARLY_PUNCH_WINDOW_ID = 'position-template-early-punch-window';
const PUNCH_ROUNDING_ID = 'position-template-punch-rounding';

const distanceFieldSx = {
  ...textFieldSx,
  width: '180px',
  '& .MuiInputBase-input': {
    fontFamily: PRIMARY_FONT[700],
    fontSize: '14px',
    fontVariantNumeric: 'tabular-nums',
  },
};

const distanceErrorSx = {
  '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
    { borderColor: RED[50] },
};

const ToggleRow = ({ titleKey, descriptionKey, checked, onChange }) => {
  const title = LanguageConverterV2(titleKey);
  const description = LanguageConverterV2(descriptionKey);

  return (
    <Box sx={{ borderBottom: `1px solid ${BLACK[20]}`, padding: '0 16px' }}>
      <SwitchRow
        text={title}
        subText={description}
        checked={!!checked}
        onChange={(event) => onChange(event.target.checked)}
        divider={false}
      />
    </Box>
  );
};

ToggleRow.propTypes = {
  titleKey: PropTypes.string.isRequired,
  descriptionKey: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const FieldMessage = ({ messageKey, isError }) => (
  <Typography
    role={isError ? 'alert' : undefined}
    sx={{
      color: isError ? RED[80] : BLACK[60],
      fontFamily: PRIMARY_FONT[400],
      fontSize: '12px',
      marginTop: '6px',
    }}
  >
    {LanguageConverterV2(messageKey)}
  </Typography>
);

FieldMessage.propTypes = {
  messageKey: PropTypes.string.isRequired,
  isError: PropTypes.bool,
};

const GpsDistancePanel = ({ value, error, onChange }) => {
  const unit = LanguageConverterV2('positionTemplate.create.additionalInfo.gpsDistance.unit');
  const [isTouched, setIsTouched] = useState(false);
  const visibleError = isTouched ? error : null;

  const handleChange = (event) => {
    const nextValue = event.target.value;
    if (PUNCH_DISTANCE_INPUT_PATTERN.test(nextValue)) {
      onChange(nextValue);
    }
  };

  return (
    <Box
      data-testid="gps-distance-panel"
      sx={{
        backgroundColor: BLACK[10],
        borderBottom: `1px solid ${BLACK[20]}`,
        padding: '8px 16px 12px 40px',
      }}
    >
      <Box sx={{ borderLeft: `2px solid ${PRIMARY_PURPLE[30]}`, paddingLeft: '16px' }}>
        <FormFieldRow
          labelKey="positionTemplate.create.additionalInfo.gpsDistance.label"
          htmlFor={GPS_DISTANCE_ID}
        >
          <WaeStandardTextField
            id={GPS_DISTANCE_ID}
            value={value}
            onChange={handleChange}
            onBlur={() => setIsTouched(true)}
            error={!!visibleError}
            InputProps={{
              inputProps: { inputMode: 'numeric', 'data-testid': GPS_DISTANCE_ID },
              endAdornment: (
                <InputAdornment position="end" sx={{ color: BLACK[60] }}>
                  {unit}
                </InputAdornment>
              ),
            }}
            sx={{ ...distanceFieldSx, ...(visibleError ? distanceErrorSx : {}) }}
          />
        </FormFieldRow>
        <FieldMessage
          messageKey={visibleError || 'positionTemplate.create.additionalInfo.gpsDistance.helper'}
          isError={!!visibleError}
        />
      </Box>
    </Box>
  );
};

GpsDistancePanel.propTypes = {
  value: PropTypes.string,
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

const PunchWindowsRow = ({ earlyPunchWindow, punchRounding, onChange }) => {
  const title = LanguageConverterV2('positionTemplate.create.additionalInfo.punchWindows.title');
  const description = LanguageConverterV2(
    'positionTemplate.create.additionalInfo.punchWindows.description'
  );

  return (
    <Box
      data-testid="punch-windows-row"
      sx={{
        alignItems: 'flex-start',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        justifyContent: 'space-between',
        padding: '12px 16px',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}>
          {title}
        </Typography>
        <Typography
          sx={{
            color: BLACK[70],
            fontFamily: PRIMARY_FONT[400],
            fontSize: '12px',
            marginTop: '2px',
          }}
        >
          {description}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: '16px' }}>
        <Box sx={{ width: '160px' }}>
          <FormFieldRow
            labelKey="positionTemplate.create.additionalInfo.earlyPunchWindow.label"
            htmlFor={EARLY_PUNCH_WINDOW_ID}
          >
            <MinutesSelectField
              id={EARLY_PUNCH_WINDOW_ID}
              value={earlyPunchWindow}
              options={EARLY_PUNCH_WINDOW_OPTIONS}
              onChange={(next) => onChange({ earlyPunchWindow: next })}
            />
          </FormFieldRow>
        </Box>
        <Box sx={{ width: '160px' }}>
          <FormFieldRow
            labelKey="positionTemplate.create.additionalInfo.punchRounding.label"
            htmlFor={PUNCH_ROUNDING_ID}
          >
            <MinutesSelectField
              id={PUNCH_ROUNDING_ID}
              value={punchRounding}
              options={PUNCH_ROUNDING_OPTIONS}
              onChange={(next) => onChange({ punchRounding: next })}
            />
          </FormFieldRow>
        </Box>
      </Box>
    </Box>
  );
};

PunchWindowsRow.propTypes = {
  earlyPunchWindow: PropTypes.number,
  punchRounding: PropTypes.number,
  onChange: PropTypes.func.isRequired,
};

const AdditionalInfoSection = ({ values, setField }) => {
  const title = LanguageConverterV2('positionTemplate.create.additionalInfo.title');
  const subtitle = LanguageConverterV2('positionTemplate.create.additionalInfo.subtitle');

  const info = values.additionalInfo || {};
  const update = (patch) => setField('additionalInfo', { ...info, ...patch });

  return (
    <Box
      id={getSectionAnchorId(SECTION_IDS.ADDITIONAL_INFO)}
      data-testid="additional-info-section"
      sx={{ scrollMarginTop: '16px' }}
    >
      <SectionCard
        header={
          <Box sx={{ alignItems: 'baseline', display: 'flex', gap: '8px' }}>
            <Typography sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}>
              {title}
            </Typography>
            <Typography sx={{ color: BLACK[60], fontFamily: PRIMARY_FONT[400], fontSize: '12px' }}>
              {subtitle}
            </Typography>
          </Box>
        }
      >
        <Box sx={{ margin: '-8px -16px' }}>
          <ToggleRow
            titleKey="positionTemplate.create.additionalInfo.employerApproval.title"
            descriptionKey="positionTemplate.create.additionalInfo.employerApproval.description"
            checked={info.requireEmployerApproval}
            onChange={(requireEmployerApproval) => update({ requireEmployerApproval })}
          />
          <ToggleRow
            titleKey="positionTemplate.create.additionalInfo.backfill.title"
            descriptionKey="positionTemplate.create.additionalInfo.backfill.description"
            checked={info.allowBackfill}
            onChange={(allowBackfill) => update({ allowBackfill })}
          />
          <ToggleRow
            titleKey="positionTemplate.create.additionalInfo.overtime.title"
            descriptionKey="positionTemplate.create.additionalInfo.overtime.description"
            checked={info.overtimeAllowed}
            onChange={(overtimeAllowed) => update({ overtimeAllowed })}
          />
          <ToggleRow
            titleKey="positionTemplate.create.additionalInfo.attendancePolicy.title"
            descriptionKey="positionTemplate.create.additionalInfo.attendancePolicy.description"
            checked={info.isEnforcingAttendancePolicy}
            onChange={(isEnforcingAttendancePolicy) => update({ isEnforcingAttendancePolicy })}
          />
          <ToggleRow
            titleKey="positionTemplate.create.additionalInfo.punchOutsideShift.title"
            descriptionKey="positionTemplate.create.additionalInfo.punchOutsideShift.description"
            checked={info.isPunchingOutsideOfShiftEnabled}
            onChange={(isPunchingOutsideOfShiftEnabled) =>
              update({ isPunchingOutsideOfShiftEnabled })
            }
          />
          <ToggleRow
            titleKey="positionTemplate.create.additionalInfo.gps.title"
            descriptionKey="positionTemplate.create.additionalInfo.gps.description"
            checked={info.isGPSEnforced}
            onChange={(isGPSEnforced) => update({ isGPSEnforced })}
          />
          {info.isGPSEnforced && (
            <GpsDistancePanel
              value={info.punchDistance}
              error={getPunchDistanceError(info)}
              onChange={(punchDistance) => update({ punchDistance })}
            />
          )}
          <ToggleRow
            titleKey="positionTemplate.create.additionalInfo.earlyAccessAvailableJobsWindow.title"
            descriptionKey="positionTemplate.create.additionalInfo.earlyAccessAvailableJobsWindow.description"
            checked={info.isEarlyAccessAvailableJobsWindowEnabled === true}
            onChange={(isEarlyAccessAvailableJobsWindowEnabled) =>
              update({ isEarlyAccessAvailableJobsWindowEnabled })
            }
          />
          <PunchWindowsRow
            earlyPunchWindow={info.earlyPunchWindow}
            punchRounding={info.punchRounding}
            onChange={update}
          />
        </Box>
      </SectionCard>
    </Box>
  );
};

AdditionalInfoSection.propTypes = {
  values: PropTypes.shape({
    additionalInfo: PropTypes.shape({
      requireEmployerApproval: PropTypes.bool,
      allowBackfill: PropTypes.bool,
      overtimeAllowed: PropTypes.bool,
      isEnforcingAttendancePolicy: PropTypes.bool,
      isPunchingOutsideOfShiftEnabled: PropTypes.bool,
      isEarlyAccessAvailableJobsWindowEnabled: PropTypes.bool,
      isGPSEnforced: PropTypes.bool,
      punchDistance: PropTypes.string,
      earlyPunchWindow: PropTypes.number,
      punchRounding: PropTypes.number,
    }),
  }).isRequired,
  setField: PropTypes.func.isRequired,
};

export default AdditionalInfoSection;
